import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";

import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { FreePlanManagement } from "@/pages/settings/components/subscription-management/free";
import { ProPlanManagement } from "@/pages/settings/components/subscription-management/pro";
import { SubscribedPlanManagement } from "@/pages/settings/components/subscription-management/subscribed";
import { CanceledPlanManagement } from "@/pages/settings/components/subscription-management/canceled";
import { LoadingSubscriptions } from "@/pages/settings/components/subscription-management/loading-subscriptions";
import { getProactiveFeatures } from "@/pages/settings/components/subscription-management/data";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { SubscriptionPlanKind } from "@/store/subscriptions/types";
import { MDSBadgeVariant } from "@/mds";
import type {
  CommercialCatalogSnapshot,
  CommercialPlanChangePreview,
  CommercialPlanChangeResult,
  CommercialPlanKey,
  CommercialPlanSnapshot,
} from "@/modules/commercial-plans";
import { CommercialPlanManagement } from "@/pages/settings/components/subscription-management/commercial-plan-management";

export const SettingsSubscriptionPlan = {
  Free: SubscriptionPlanKind.FREE,
  Pro: SubscriptionPlanKind.PRO,
  Proactive: SubscriptionPlanKind.PROACTIVE,
} as const;
export type SettingsSubscriptionPlan =
  (typeof SettingsSubscriptionPlan)[keyof typeof SettingsSubscriptionPlan];

interface SettingsSubscriptionManagementPageViewProps {
  isLoading: boolean;
  plan: SettingsSubscriptionPlan;
  renewalDateText: string;
  downgradeBadgeLabel?: string;
  downgradePreposition?: string;
  planEndsText?: string;
  grantedAccessStatusText?: string;
  effectivePlanUsesGrantedAccess: boolean;
  isProactiveTrialEligible: boolean;
  proactivePriceText: string;
  canManagePlan: boolean;
  subscribingPlan?: SettingsSubscriptionPlan | null;
  commercialCatalog?: CommercialCatalogSnapshot | null;
  commercialPlanSnapshot?: CommercialPlanSnapshot | null;
  changingCommercialPlanKey?: CommercialPlanKey | null;
  initialCommercialPlanKey?: CommercialPlanKey | null;
  usageProgress?: ReactNode;
  onSubscribe: (billingStrategy: BillingStrategyEnum) => void;
  onSubscribeToProactive: () => void;
  onManagePlan: () => void;
  onApplyCommercialPlanChange?: (args: {
    idempotencyKey: string;
    preview: CommercialPlanChangePreview;
  }) => Promise<CommercialPlanChangeResult>;
  onPreviewCommercialPlanChange?: (args: {
    paidExtraUsageBlocks: number;
    planKey: CommercialPlanKey;
  }) => Promise<CommercialPlanChangePreview>;
  onStartCommercialSubscription?: (args: {
    paidExtraUsageBlocks: number;
    planKey: CommercialPlanKey;
  }) => void;
}

export const SettingsSubscriptionManagementPageView = observer(
  ({
    isLoading,
    plan,
    renewalDateText,
    downgradeBadgeLabel,
    downgradePreposition,
    planEndsText,
    grantedAccessStatusText,
    effectivePlanUsesGrantedAccess,
    isProactiveTrialEligible,
    proactivePriceText,
    canManagePlan,
    subscribingPlan = null,
    commercialCatalog = null,
    commercialPlanSnapshot = null,
    changingCommercialPlanKey = null,
    initialCommercialPlanKey = null,
    usageProgress,
    onSubscribe,
    onSubscribeToProactive,
    onManagePlan,
    onApplyCommercialPlanChange,
    onPreviewCommercialPlanChange,
    onStartCommercialSubscription,
  }: SettingsSubscriptionManagementPageViewProps) => {
    return (
      <SettingsIntegrationPageWrapper>
        <SettingsIntegrationHeader
          breadcrumb="Account"
          title="Subscription management"
          badgeLabel={plan === SettingsSubscriptionPlan.Proactive ? downgradeBadgeLabel : undefined}
          badgeVariant={downgradeBadgeLabel ? MDSBadgeVariant.Negative : undefined}
        />
        <SettingsIntegrationScrollableArea>
          <SettingsIntegrationSection>
            {isLoading ? (
              <LoadingSubscriptions />
            ) : commercialPlanSnapshot ? (
              <CommercialPlanManagement
                catalog={commercialCatalog}
                snapshot={commercialPlanSnapshot}
                isChangingPlan={changingCommercialPlanKey !== null}
                initialSelectedPlanKey={initialCommercialPlanKey}
                onManageBilling={canManagePlan ? onManagePlan : undefined}
                onApplyPlanChange={onApplyCommercialPlanChange}
                onPreviewPlanChange={onPreviewCommercialPlanChange}
                onStartSubscription={onStartCommercialSubscription}
              />
            ) : plan === SettingsSubscriptionPlan.Free ? (
              <FreePlanManagement
                renewalDateText={renewalDateText}
                usageProgress={usageProgress}
                onSubscribe={onSubscribe}
                onSubscribeToProactive={onSubscribeToProactive}
                isProactiveTrialEligible={isProactiveTrialEligible}
                proactivePriceText={proactivePriceText}
                isSubscribingToPro={subscribingPlan === SettingsSubscriptionPlan.Pro}
                isSubscribingToProactive={subscribingPlan === SettingsSubscriptionPlan.Proactive}
              />
            ) : plan === SettingsSubscriptionPlan.Proactive ? (
              <SubscribedPlanManagement
                showPlanHeader={false}
                title="Proactive"
                priceText={effectivePlanUsesGrantedAccess ? "Included by Mem" : proactivePriceText}
                features={getProactiveFeatures()}
                statusText={grantedAccessStatusText ?? planEndsText ?? ""}
                titleBadgeLabel={effectivePlanUsesGrantedAccess ? "On the house" : null}
                manageButtonLabel={
                  effectivePlanUsesGrantedAccess ? "Manage billing" : "Manage your Proactive Plan"
                }
                showManageButton={canManagePlan}
                showExternalLinkIcon
                maxWidth="500px"
                onManagePlan={onManagePlan}
              />
            ) : downgradeBadgeLabel ? (
              <CanceledPlanManagement
                subscriptionStatusText={planEndsText ?? ""}
                downgradePreposition={downgradePreposition ?? "soon"}
                onSubscribe={() => onSubscribe(BillingStrategyEnum.MONTHLY)}
              />
            ) : (
              <ProPlanManagement
                canManagePlan={canManagePlan}
                currentPlanPriceText={
                  effectivePlanUsesGrantedAccess ? "Included by Mem" : "$12 / month"
                }
                currentPlanStatusText={grantedAccessStatusText ?? planEndsText ?? "Unlimited usage"}
                currentPlanTitleBadgeLabel={
                  effectivePlanUsesGrantedAccess ? "On the house" : "Current"
                }
                isProactiveTrialEligible={isProactiveTrialEligible}
                proactivePriceText={proactivePriceText}
                isSubscribingToProactive={subscribingPlan === SettingsSubscriptionPlan.Proactive}
                onManagePlan={onManagePlan}
                onSubscribeToProactive={onSubscribeToProactive}
              />
            )}
          </SettingsIntegrationSection>
        </SettingsIntegrationScrollableArea>
      </SettingsIntegrationPageWrapper>
    );
  }
);
